import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Minimize2, Maximize2, Send, Paperclip, Ticket, Loader2, Bell, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FloatingMascot({ userId, userName, userEmail }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [position, setPosition] = useState({ x: 20, y: window.innerHeight - 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Chat state
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Ticket state
  const [tickets, setTickets] = useState([]);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '' });
  const [ticketScreenshot, setTicketScreenshot] = useState(null);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load conversation and messages
  useEffect(() => {
    if (userId && isOpen) {
      loadConversation();
      loadTickets();
    }
  }, [userId, isOpen]);

  // Poll for new messages
  useEffect(() => {
    if (userId && isOpen && conversation) {
      const interval = setInterval(() => {
        loadMessages();
        loadUnreadCount();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [userId, isOpen, conversation]);

  // Poll for unread count when closed
  useEffect(() => {
    if (userId && !isOpen) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [userId, isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversation = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/conversation/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setConversation(data);
        loadMessages(data.id);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const loadMessages = async (convId = conversation?.id) => {
    if (!convId) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/messages/${convId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        // Mark as read
        await fetch(`${API_URL}/api/chat/mark-read/${convId}?reader_type=user`, { method: 'POST' });
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/unread-count/${userId}?is_admin=false`);
      if (res.ok) {
        const data = await res.json();
        if (data.unread_count > unreadCount && !isOpen) {
          // Play notification sound
          playNotificationSound();
        }
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadTickets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tickets?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Failed to load tickets:', error);
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telezo2kNPdq2kxJJnH2qRwRS+RvtuneEsym7LYoX5TN52s1Z2EWjuio9OYim05np3OlZFqPpuTyZGXb0CakceOnHVBmo7Ej6F5Qpp5vYmkfUWad7aEqIFGmnOugqyDSJlwqH+vhUqZbaN8sYdLmWqee7OJTJlonHq1i02ZaJp5totOmWiZeLiLT5lomXe5i0+ZaJl3uYtPmWiZd7mLT5lomXe5i0+ZaJl3uYtPmWiZd7mLT5lomXe5i0+ZaJl3uYtPmWiZd7mLT5lomXe5i0+ZaJl3uYtPmWiZd7mLT5lomXe5i0+ZaJl3uYtPmWiZd7mLT5lo');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {
      // Sound play failed, silently ignore
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !conversation) return;
    
    setIsSending(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/send?conversation_id=${conversation.id}&sender_id=${userId}&sender_name=${encodeURIComponent(userName)}&sender_type=user&message=${encodeURIComponent(newMessage)}`, {
        method: 'POST'
      });
      if (res.ok) {
        setNewMessage('');
        loadMessages();
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const createTicket = async () => {
    if (!ticketForm.subject || !ticketForm.description) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsCreatingTicket(true);
    try {
      let screenshotUrl = null;
      if (ticketScreenshot) {
        const formData = new FormData();
        formData.append('file', ticketScreenshot);
        const uploadRes = await fetch(`${API_URL}/api/tickets/upload-screenshot`, {
          method: 'POST',
          body: formData
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          screenshotUrl = uploadData.url;
        }
      }
      
      const res = await fetch(`${API_URL}/api/tickets?user_id=${userId}&user_name=${encodeURIComponent(userName)}&subject=${encodeURIComponent(ticketForm.subject)}&description=${encodeURIComponent(ticketForm.description)}&user_email=${encodeURIComponent(userEmail || '')}&screenshot_url=${encodeURIComponent(screenshotUrl || '')}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Ticket created successfully');
        setShowNewTicket(false);
        setTicketForm({ subject: '', description: '' });
        setTicketScreenshot(null);
        loadTickets();
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setIsCreatingTicket(false);
    }
  };

  const sendTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/respond?responder_id=${userId}&responder_name=${encodeURIComponent(userName)}&responder_type=user&message=${encodeURIComponent(ticketReply)}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        setTicketReply('');
        // Reload ticket details
        const ticketRes = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}`);
        if (ticketRes.ok) {
          const data = await ticketRes.json();
          setSelectedTicket({ ...data.ticket, responses: data.responses });
        }
      }
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    if (isOpen) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: Math.max(0, Math.min(window.innerWidth - 60, e.clientX - dragOffset.x)),
      y: Math.max(0, Math.min(window.innerHeight - 60, e.clientY - dragOffset.y))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'resolved': return 'bg-green-500';
      case 'closed': return 'bg-muted/500';
      default: return 'bg-muted/500';
    }
  };

  return (
    <>
      {/* Floating Mascot Button */}
      <div
        className={`fixed z-50 cursor-pointer transition-all duration-200 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
      >
        <div
          className="relative"
          onClick={() => !isDragging && setIsOpen(true)}
        >
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow">
            <span className="text-3xl">🤖</span>
          </div>
          {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
              {unreadCount}
            </div>
          )}
        </div>
      </div>

      {/* Chat/Ticket Panel */}
      {isOpen && (
        <div
          className={`fixed z-50 bg-card rounded-xl shadow-2xl border overflow-hidden transition-all duration-200 ${isMinimized ? 'h-14' : 'h-[500px]'}`}
          style={{
            right: 20,
            bottom: 20,
            width: isMinimized ? 280 : 380
          }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <span className="text-xl">🤖</span>
              <span className="font-semibold">Support Center</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white hover:bg-card/20"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white hover:bg-card/20"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {!isMinimized && (
            <div className="flex flex-col h-[calc(100%-56px)]">
              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
                <TabsList className="grid w-full grid-cols-2 rounded-none border-b">
                  <TabsTrigger value="chat" className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Chat
                    {unreadCount > 0 && (
                      <Badge variant="destructive" className="h-5 px-1.5 text-xs">{unreadCount}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="tickets" className="flex items-center gap-2">
                    <Ticket className="h-4 w-4" />
                    Tickets
                    {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* Chat Tab */}
                <TabsContent value="chat" className="flex-1 flex flex-col m-0 p-0">
                  <ScrollArea className="flex-1 p-3">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Start a conversation with admin</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] px-3 py-2 rounded-lg ${
                                msg.sender_type === 'user'
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-muted text-foreground'
                              }`}
                            >
                              <p className="text-sm">{msg.message}</p>
                              <p className={`text-xs mt-1 ${msg.sender_type === 'user' ? 'text-blue-100' : 'text-muted-foreground'}`}>
                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>
                  
                  <div className="p-3 border-t">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        className="flex-1"
                      />
                      <Button onClick={sendMessage} disabled={isSending || !newMessage.trim()}>
                        {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                {/* Tickets Tab */}
                <TabsContent value="tickets" className="flex-1 flex flex-col m-0 p-0">
                  {selectedTicket ? (
                    // Ticket Detail View
                    <div className="flex flex-col h-full">
                      <div className="p-3 border-b flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)}>
                          ← Back
                        </Button>
                        <span className={`w-2 h-2 rounded-full ${getStatusColor(selectedTicket.status)}`} />
                        <span className="text-sm font-medium truncate">{selectedTicket.subject}</span>
                      </div>
                      <ScrollArea className="flex-1 p-3">
                        <div className="space-y-3">
                          {/* Original ticket */}
                          <div className="bg-muted/50 p-3 rounded-lg">
                            <p className="text-sm">{selectedTicket.description}</p>
                            {selectedTicket.screenshot_url && (
                              <img 
                                src={`${API_URL}${selectedTicket.screenshot_url}`} 
                                alt="Screenshot" 
                                className="mt-2 rounded border max-h-40 object-contain"
                              />
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              {new Date(selectedTicket.created_at).toLocaleString()}
                            </p>
                          </div>
                          {/* Responses */}
                          {selectedTicket.responses?.map((resp) => (
                            <div
                              key={resp.id}
                              className={`p-3 rounded-lg ${
                                resp.responder_type === 'user' ? 'bg-blue-50 ml-4' : 'bg-green-50 mr-4'
                              }`}
                            >
                              <p className="text-xs font-medium text-muted-foreground mb-1">
                                {resp.responder_type === 'admin' ? '👤 Admin' : 'You'}
                              </p>
                              <p className="text-sm">{resp.message}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(resp.created_at).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                        <div className="p-3 border-t">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Add a reply..."
                              value={ticketReply}
                              onChange={(e) => setTicketReply(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && sendTicketReply()}
                            />
                            <Button onClick={sendTicketReply} disabled={!ticketReply.trim()}>
                              <Send className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : showNewTicket ? (
                    // New Ticket Form
                    <div className="p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Create Ticket</h4>
                        <Button variant="ghost" size="sm" onClick={() => setShowNewTicket(false)}>
                          Cancel
                        </Button>
                      </div>
                      <Input
                        placeholder="Subject"
                        value={ticketForm.subject}
                        onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                      />
                      <Textarea
                        placeholder="Describe your issue..."
                        value={ticketForm.description}
                        onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                        rows={4}
                      />
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => setTicketScreenshot(e.target.files?.[0])}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full"
                        >
                          <Paperclip className="h-4 w-4 mr-2" />
                          {ticketScreenshot ? ticketScreenshot.name : 'Attach Screenshot'}
                        </Button>
                      </div>
                      <Button onClick={createTicket} disabled={isCreatingTicket} className="w-full">
                        {isCreatingTicket ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Submit Ticket
                      </Button>
                    </div>
                  ) : (
                    // Ticket List
                    <>
                      <ScrollArea className="flex-1">
                        {tickets.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Ticket className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No tickets yet</p>
                          </div>
                        ) : (
                          <div className="p-2 space-y-2">
                            {tickets.map((ticket) => (
                              <div
                                key={ticket.id}
                                className="p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                                onClick={async () => {
                                  const res = await fetch(`${API_URL}/api/tickets/${ticket.id}`);
                                  if (res.ok) {
                                    const data = await res.json();
                                    setSelectedTicket({ ...data.ticket, responses: data.responses });
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${getStatusColor(ticket.status)}`} />
                                  <span className="font-medium text-sm truncate">{ticket.subject}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{ticket.description}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {new Date(ticket.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                      <div className="p-3 border-t">
                        <Button onClick={() => setShowNewTicket(true)} className="w-full">
                          <Ticket className="h-4 w-4 mr-2" />
                          Create New Ticket
                        </Button>
                      </div>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      )}
    </>
  );
}
